import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {fetchWithAuth} from '../../core/utils/fetchWithAuth'
import {PageTitle} from '../../../_metronic/layout/core'
import {KTIcon} from '../../../_metronic/helpers'
import type {Cargo, ContactoSocio} from './contactoSocio'
import {getContactosSecundarios} from './contactoSocio'
import {SocioContactosModal} from './components/SocioContactosModal'

// ============================================================================
// TIPOS
// ============================================================================

type Movimiento = {
  id: string
  nroFactura: string
  idPago: string
  fecha: string
  monto: string
  tipo: string
  estado: string
  medioPago?: string | null
  numeroRecibo?: string | null
  comprobante?: string
}

type Giro = {
  id: number
  nombre: string
  descripcion: string | null
  activo: boolean
  principal: boolean
}

type GiroDisponible = {
  id: number
  nombre: string
  descripcion: string | null
  estado: boolean
}

type Categoria = {
  id: number
  nombre: string
  descripcion: string
}

type Departamento = {
  id: number
  nombre: string
}

type Localidad = {
  id: number
  nombre: string
  departamentoId: number
  codigoPostal: string
  codigoZona: string
  municipio: string | null
  geo: string | null
}

type Cuenta = {
  numero: number
  categoria: string
  zonaCobro: string
  activa: string
  exentoIVA?: boolean
  exentoIRAE?: boolean
  saldoInicial?: number
  movimientos: Movimiento[]
}

type Contacto = ContactoSocio

type Attachment = {
  id: number
  purpose: string
  contentType: string
  filename: string
  sizeBytes: number
  url: string
  thumbUrl: string
}

type MotivoBaja = {
  id: number
  nombre: string
  descripcion: string
}

type SocioDetalle = {
  id: string
  idMiembro: string
  nroSocio?: number
  nombreFantasia: string
  razonSocial: string
  rut: string
  bps: string
  mtss: string | null
  direccion: string
  telefono: string
  ciudad: string | null
  calle: string | null
  numeroPuerta: string | null
  giros: Giro[]
  activo: string
  esSocio: number // 0 = deshabilitado, 1 = habilitado
  contactoNombre: string
  contactoTelefono: string
  contactoEmail: string
  foto: string | null
  especialidad: string
  categoria: Categoria
  cuenta?: Cuenta
  contactos?: Contacto[]
  attachments?: Attachment[]
}

type FormData = {
  nombreFantasia: string
  razonSocial: string
  rut: string
  bps: string
  mtss: string
  departamentoId: string
  localidadId: string
  direccion: string
  calle: string
  numeroPuerta: string
  comentario_direccion: string
  ciudad: string
  telefono: string
  giros: string
  contactoNombre: string
  contactoTelefono: string
  contactoEmail: string
  especialidad: string
  tipoMiembroNombre: string
  clase: string
  claseId: string
  nroSocio: string
  categoria: string
  zonaCobro: string
  activa: string
  exentaIva: boolean
  exentaIrae: boolean
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

const InfoRow: React.FC<{label: string; value: React.ReactNode; icon?: string}> = ({
  label,
  value,
  icon,
}) => (
  <div className='d-flex align-items-center py-3 border-bottom border-gray-300'>
    {icon && (
      <div className='me-4'>
        <KTIcon iconName={icon} className='fs-1 text-primary' />
      </div>
    )}
    <div className='flex-grow-1'>
      <div className='text-gray-600 fw-semibold fs-7'>{label}</div>
      <div className='text-gray-800 fw-bold fs-6'>{value || 'No especificado'}</div>
    </div>
  </div>
)

const EditableInfoRow: React.FC<{
  label: string
  value: string
  onChange: (value: string) => void
  icon?: string
  required?: boolean
  type?: string
}> = ({label, value, onChange, icon, required = false, type = 'text'}) => (
  <div className='d-flex align-items-center py-3 border-bottom border-gray-300'>
    {icon && (
      <div className='me-4'>
        <KTIcon iconName={icon} className='fs-1 text-primary' />
      </div>
    )}
    <div className='flex-grow-1'>
      <label className='text-gray-600 fw-semibold fs-7'>
        {label} {required && <span className='text-danger'>*</span>}
      </label>
      <input
        type={type}
        className='form-control form-control-sm mt-1'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
    </div>
  </div>
)

const SectionCard: React.FC<{
  title: string
  icon?: string
  children: React.ReactNode
  className?: string
  headerActions?: React.ReactNode
}> = ({title, icon, children, className = '', headerActions}) => (
  <div className={`card ${className}`}>
    <div className='card-header'>
      <div className='card-title d-flex flex-wrap align-items-center justify-content-between gap-3 w-100'>
        <span className='card-label fw-bold text-dark d-flex align-items-center'>
          {icon && <KTIcon iconName={icon} className='fs-1 me-2' />}
          {title}
        </span>
        {headerActions ? <div className='card-toolbar'>{headerActions}</div> : null}
      </div>
    </div>
    <div className='card-body'>{children}</div>
  </div>
)

const GirosSelector: React.FC<{
  selectedGiros: number[]
  availableGiros: GiroDisponible[]
  loading: boolean
  onChange: (giroIds: number[]) => void
  onSearch: (query: string) => void
}> = ({selectedGiros, availableGiros, loading, onChange, onSearch}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  // Cache de todos los giros que se han cargado para mantener la referencia a los seleccionados
  const [girosCache, setGirosCache] = useState<Map<number, GiroDisponible>>(new Map())

  // Actualizar el cache con los nuevos giros disponibles
  useEffect(() => {
    setGirosCache((prevCache) => {
      const newCache = new Map(prevCache)
      availableGiros.forEach((giro) => {
        if (!newCache.has(giro.id)) {
          newCache.set(giro.id, giro)
        }
      })
      return newCache
    })
  }, [availableGiros])

  const filteredGiros = useMemo(() => {
    if (!searchQuery) return availableGiros
    return availableGiros.filter((g) => g.nombre.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [availableGiros, searchQuery])

  const handleAddGiro = (giroId: number) => {
    if (!selectedGiros.includes(giroId)) {
      onChange([...selectedGiros, giroId])
    }
    setSearchQuery('')
    setShowDropdown(false)
  }

  const handleRemoveGiro = (giroId: number) => {
    onChange(selectedGiros.filter((id) => id !== giroId))
  }

  // Usar el cache para obtener los giros seleccionados, así siempre se muestran
  const selectedGirosData = selectedGiros
    .map((id) => girosCache.get(id))
    .filter((giro): giro is GiroDisponible => giro !== undefined)

  return (
    <div className='py-3'>
      <label className='text-gray-600 fw-semibold fs-7 mb-2'>
        Giros <span className='text-danger'>*</span>
      </label>

      {/* Giros Seleccionados */}
      <div className='d-flex flex-wrap gap-2 mb-3'>
        {selectedGirosData.length > 0 ? (
          selectedGirosData.map((giro) => (
            <span key={giro.id} className='badge badge-light-primary badge-lg'>
              <span className='me-2'>{giro.nombre}</span>
              <button
                type='button'
                className='btn btn-icon btn-sm btn-active-light-primary'
                onClick={() => handleRemoveGiro(giro.id)}
                style={{padding: 0, width: '16px', height: '16px'}}
              >
                <KTIcon iconName='cross' className='fs-5' />
              </button>
            </span>
          ))
        ) : (
          <span className='text-muted fs-7'>No hay giros seleccionados</span>
        )}
      </div>

      {/* Buscador de Giros */}
      <div className='position-relative'>
        <input
          type='text'
          className='form-control'
          placeholder='Buscar giro para agregar...'
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            onSearch(e.target.value)
            setShowDropdown(true)
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        />

        {/* Dropdown de Resultados */}
        {showDropdown && searchQuery && (
          <div
            className='dropdown-menu show w-100 p-2'
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1050,
            }}
          >
            {loading ? (
              <div className='text-center py-3'>
                <div className='spinner-border spinner-border-sm text-primary' />
                <div className='text-muted fs-7 mt-2'>Buscando giros...</div>
              </div>
            ) : filteredGiros.length > 0 ? (
              filteredGiros.map((giro) => {
                const isSelected = selectedGiros.includes(giro.id)
                return (
                  <button
                    key={giro.id}
                    type='button'
                    className={`dropdown-item rounded d-flex align-items-center justify-content-between ${
                      isSelected ? 'active' : ''
                    }`}
                    onClick={() => !isSelected && handleAddGiro(giro.id)}
                    disabled={isSelected}
                  >
                    <span>{giro.nombre}</span>
                    {isSelected && <KTIcon iconName='check' className='fs-3 text-success' />}
                  </button>
                )
              })
            ) : (
              <div className='text-center py-3'>
                <KTIcon iconName='file-deleted' className='fs-2x text-gray-400 mb-2' />
                <div className='text-muted fs-7'>No se encontraron giros</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className='form-text mt-2'>
        Busca y selecciona los giros comerciales del socio. Los giros seleccionados aparecerán
        arriba.
      </div>
    </div>
  )
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

function SocioDetallePage() {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()

  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [socio, setSocio] = useState<SocioDetalle | null>(null)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [togglingStatus, setTogglingStatus] = useState<boolean>(false)

  // Modal Desasociar: motivos de baja, formulario y envío
  const [desasociarModalOpen, setDesasociarModalOpen] = useState<boolean>(false)
  const [motivosBaja, setMotivosBaja] = useState<MotivoBaja[]>([])
  const [loadingMotivosBaja, setLoadingMotivosBaja] = useState<boolean>(false)
  const [desasociarForm, setDesasociarForm] = useState<{
    motivoId: string
    observaciones: string
    fechaReintegro: string
  }>({motivoId: '', observaciones: '', fechaReintegro: ''})
  const [submittingDesasociar, setSubmittingDesasociar] = useState<boolean>(false)

  // Estado para modal de contacto
  const [contactoModalOpen, setContactoModalOpen] = useState<boolean>(false)
  const [contactosListaModalOpen, setContactosListaModalOpen] = useState<boolean>(false)
  const [contactoEditing, setContactoEditing] = useState<Contacto | null>(null)
  const [savingContacto, setSavingContacto] = useState<boolean>(false)

  // Estado para validación RUT / nroSocio
  const [validationModal, setValidationModal] = useState<{
    show: boolean
    type: 'rut' | 'nroSocio' | null
    exists: boolean
    nombre: string
    value: string
  }>({
    show: false,
    type: null,
    exists: false,
    nombre: '',
    value: '',
  })
  const [validating, setValidating] = useState<boolean>(false)
  const [verifyingNroSocio, setVerifyingNroSocio] = useState<boolean>(false)

  // Estado del formulario de contacto
  const [contactoFormData, setContactoFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    documentoIdentidad: '',
    idCargo: '',
    activo: true,
    esAdmin: false,
    principal: false,
  })

  const [cargosDisponibles, setCargosDisponibles] = useState<Cargo[]>([])
  const [loadingCargos, setLoadingCargos] = useState<boolean>(false)

  // Estado del formulario
  const [formData, setFormData] = useState<FormData>({
    nombreFantasia: '',
    razonSocial: '',
    rut: '',
    bps: '',
    mtss: '',
    departamentoId: '',
    localidadId: '',
    direccion: '',
    calle: '',
    numeroPuerta: '',
    comentario_direccion: '',
    ciudad: '',
    telefono: '',
    giros: '',
    contactoNombre: '',
    contactoTelefono: '',
    contactoEmail: '',
    especialidad: '',
    tipoMiembroNombre: 'Potencial',
    clase: 'Empresa',
    claseId: '',
    nroSocio: '',
    categoria: '',
    zonaCobro: '',
    activa: 'Activa',
    exentaIva: false,
    exentaIrae: false,
  })

  // Estado para giros
  const [girosDisponibles, setGirosDisponibles] = useState<GiroDisponible[]>([])
  const [loadingGiros, setLoadingGiros] = useState<boolean>(false)
  const [selectedGiroIds, setSelectedGiroIds] = useState<number[]>([])

  // Estado para categorías
  const [categorias, setCategorias] = useState<Categoria[]>([])
  const [loadingCategorias, setLoadingCategorias] = useState<boolean>(false)

  // Estados para ubicación
  const [departamentos, setDepartamentos] = useState<Departamento[]>([])
  const [loadingDepartamentos, setLoadingDepartamentos] = useState<boolean>(false)
  const [localidades, setLocalidades] = useState<Localidad[]>([])
  const [localidadesFiltradas, setLocalidadesFiltradas] = useState<Localidad[]>([])
  const [loadingLocalidades, setLoadingLocalidades] = useState<boolean>(false)
  const [busquedaLocalidad, setBusquedaLocalidad] = useState<string>('')

  const getToken = (): string | null => localStorage.getItem('X-TOKEN')

  const fetchGiros = async (query?: string) => {
    try {
      setLoadingGiros(true)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getGiros',
          activo: '1',
          ...(query && query.length > 0 ? {q: query} : {}),
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        setGirosDisponibles(data.data.giros || [])
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      }
    } catch (err) {
      console.error('Error al cargar giros:', err)
    } finally {
      setLoadingGiros(false)
    }
  }

  const fetchCategorias = async () => {
    try {
      setLoadingCategorias(true)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getCategorias',
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        setCategorias(data.data.categorias || [])
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      }
    } catch (err) {
      console.error('Error al cargar categorías:', err)
    } finally {
      setLoadingCategorias(false)
    }
  }

  // Fetch Departamentos
  const fetchDepartamentos = async () => {
    try {
      setLoadingDepartamentos(true)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getDepartamentos',
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        setDepartamentos(data.data || [])
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      }
    } catch (err) {
      console.error('Error al cargar departamentos:', err)
    } finally {
      setLoadingDepartamentos(false)
    }
  }

  // Fetch Localidades
  const fetchLocalidades = async () => {
    try {
      setLoadingLocalidades(true)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getLocalidades',
          includeDepartamento: false,
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        setLocalidades(data.data || [])
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      }
    } catch (err) {
      console.error('Error al cargar localidades:', err)
    } finally {
      setLoadingLocalidades(false)
    }
  }

  const fetchCargos = async () => {
    try {
      setLoadingCargos(true)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getCargos',
          current_page: 1,
          page_size: 100,
          activo: 1,
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        const list = (data.data?.cargos || []) as Cargo[]
        setCargosDisponibles(list.filter((c) => c.estado !== false))
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      }
    } catch (err) {
      console.error('Error al cargar cargos:', err)
    } finally {
      setLoadingCargos(false)
    }
  }

  const fetchSocio = async (idToFetch: string) => {
    try {
      setLoading(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getSocio',
          id: idToFetch,
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        const socioData = data.data as SocioDetalle
        setSocio(socioData)

        // Inicializar el formulario con los datos del socio
        const giroIds = socioData.giros?.map((g) => g.id) || []
        setSelectedGiroIds(giroIds)

        setFormData({
          nombreFantasia: socioData.nombreFantasia || '',
          razonSocial: socioData.razonSocial || '',
          rut: socioData.rut || '',
          bps: socioData.bps || '',
          mtss: socioData.mtss || '',
          departamentoId: '',
          localidadId: '',
          direccion: socioData.direccion || '',
          calle: socioData.calle || '',
          numeroPuerta: socioData.numeroPuerta || '',
          comentario_direccion: '',
          ciudad: socioData.ciudad || '',
          telefono: socioData.telefono || '',
          giros: giroIds.join(','),
          contactoNombre: socioData.contactoNombre || '',
          contactoTelefono: socioData.contactoTelefono || '',
          contactoEmail: socioData.contactoEmail || '',
          especialidad: socioData.especialidad || '',
          tipoMiembroNombre: 'Potencial',
          clase: 'Empresa',
          claseId: '',
          nroSocio: socioData.nroSocio?.toString() || '',
          categoria: socioData.categoria?.nombre || '',
          zonaCobro: String(socioData.cuenta?.zonaCobro ?? ''),
          activa:
            socioData.cuenta?.activa === '1'
              ? 'Activa'
              : socioData.cuenta?.activa === '0'
              ? 'Inactiva'
              : 'Activa',
          exentaIva: !!socioData.cuenta?.exentoIVA,
          exentaIrae: !!socioData.cuenta?.exentoIRAE,
        })
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
        return
      } else {
        throw new Error(data.returned_message || 'Error en la respuesta del servidor')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!socio) return

    try {
      setSaving(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const ciudadVal =
        formData.ciudad ||
        localidades.find((l) => l.id.toString() === formData.localidadId)?.nombre ||
        ''
      const codigoZonaNum = formData.zonaCobro ? parseInt(formData.zonaCobro, 10) : undefined
      const nroSocioNum = formData.nroSocio ? parseInt(formData.nroSocio, 10) : undefined
      const claseIdNum = formData.claseId ? parseInt(formData.claseId, 10) : undefined

      // 1) updateMiembro con todos los parámetros que acepta el servicio
      const updateMiembroPayload: Record<string, unknown> = {
        service: 'updateMiembro',
        id: parseInt(socio.idMiembro, 10),
        tipoMiembroNombre: formData.tipoMiembroNombre || 'Potencial',
        activa: formData.activa,
        clase: formData.clase || 'Empresa',
        nroSocio: nroSocioNum ?? socio.nroSocio,
        esSocio: socio.esSocio === 1,
        tipoSocio: formData.categoria || socio.categoria?.nombre || '',
        nombreFantasia: formData.nombreFantasia,
        razonSocial: formData.razonSocial,
        rut: formData.rut,
        bps: formData.bps,
        mtss: formData.mtss || '',
        direccion: formData.direccion || '',
        telefono: formData.telefono || '',
        calle: formData.calle,
        numeroPuerta: formData.numeroPuerta,
        ciudad: ciudadVal,
        giros: selectedGiroIds.length > 0 ? selectedGiroIds.join('-') : '',
        contactoNombre: formData.contactoNombre,
        contactoTelefono: formData.contactoTelefono,
        contactoEmail: formData.contactoEmail,
        especialidad: formData.especialidad || '',
      }
      if (codigoZonaNum !== undefined && !isNaN(codigoZonaNum)) {
        updateMiembroPayload.codigoZona = codigoZonaNum
      }
      if (claseIdNum !== undefined && !isNaN(claseIdNum)) {
        updateMiembroPayload.claseId = claseIdNum
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify(updateMiembroPayload),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
        return
      }
      if (data.code !== 'OK') {
        throw new Error(data.returned_message || 'Error al actualizar el socio')
      }

      // 2) Si tiene cuenta, actualizar también con updateCuenta
      const cuenta = socio.cuenta
      if (cuenta) {
        const categoriaId = formData.categoria
          ? categorias.find((c) => c.nombre === formData.categoria)?.id
          : undefined
        const zonaCobroNum = formData.zonaCobro ? parseInt(formData.zonaCobro, 10) : 0
        const updateCuentaPayload = {
          service: 'updateCuenta',
          numero: cuenta.numero,
          activa: formData.activa === 'Activa' ? 1 : 0,
          categoria: categoriaId ?? (parseInt(String(cuenta.categoria), 10) || 0),
          zonaCobro: zonaCobroNum,
          exentaIva: formData.exentaIva ? 1 : 0,
          exentaIrae: formData.exentaIrae ? 1 : 0,
          conceptos: [],
        }
        const resCuenta = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-TOKEN': token,
          },
          body: JSON.stringify(updateCuentaPayload),
        })
        if (!resCuenta.ok) throw new Error(`Error al actualizar la cuenta: ${resCuenta.status}`)
        const dataCuenta = await resCuenta.json()
        if (dataCuenta.code === 'ERROR' && dataCuenta.returned_message?.includes('JWT expired')) {
          localStorage.removeItem('X-TOKEN')
          navigate('/auth/login')
          return
        }
        if (dataCuenta.code !== 'OK') {
          throw new Error(dataCuenta.returned_message || 'Error al actualizar la cuenta')
        }
      }

      await fetchSocio(id!)
      setIsEditing(false)

      const toast = document.createElement('div')
      toast.className =
        'toast align-items-center text-bg-success border-0 position-fixed top-0 end-0 m-3'
      toast.style.zIndex = '9999'
      toast.innerHTML = `
        <div class="d-flex">
          <div class="toast-body">
            <i class="bi bi-check-circle me-2"></i>
            Socio actualizado correctamente
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
        </div>
      `
      document.body.appendChild(toast)
      setTimeout(() => toast.remove(), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setSaving(false)
    }
  }

  const fetchMotivosBaja = async () => {
    try {
      setLoadingMotivosBaja(true)
      const token = getToken()
      if (!token) return
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'X-TOKEN': token},
        body: JSON.stringify({service: 'getMotivosBaja'}),
      })
      if (!response.ok) return
      const data = await response.json()
      if (data.code === 'OK' && data.data?.motivos) {
        setMotivosBaja(Array.isArray(data.data.motivos) ? data.data.motivos : [])
      }
    } catch (err) {
      console.error('Error al cargar motivos de baja:', err)
    } finally {
      setLoadingMotivosBaja(false)
    }
  }

  const handleOpenDesasociarModal = () => {
    setDesasociarForm({motivoId: '', observaciones: '', fechaReintegro: ''})
    setDesasociarModalOpen(true)
    fetchMotivosBaja()
  }

  const handleCloseDesasociarModal = () => {
    setDesasociarModalOpen(false)
    setDesasociarForm({motivoId: '', observaciones: '', fechaReintegro: ''})
  }

  const handleToggleSocioStatus = async () => {
    if (!socio) return
    if (socio.esSocio === 1) {
      handleOpenDesasociarModal()
      return
    }
    await doAfiliarDesafiliar(true)
  }

  const doAfiliarDesafiliar = async (
    esSocio: boolean,
    payloadExtra?: {
      motivoId: number
      fechaBaja: string
      observaciones: string
      fechaReintegro?: string
    }
  ) => {
    if (!socio) return
    try {
      setTogglingStatus(true)
      if (!esSocio) setSubmittingDesasociar(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const body: Record<string, unknown> = {
        service: 'afiliarDesafiliarMiembro',
        idMiembro: parseInt(socio.idMiembro, 10),
        esSocio,
      }
      if (!esSocio && payloadExtra) {
        body.motivoId = payloadExtra.motivoId
        body.fechaBaja = payloadExtra.fechaBaja
        body.observaciones = payloadExtra.observaciones
        if (payloadExtra.fechaReintegro) {
          body.fechaReintegro = payloadExtra.fechaReintegro
        }
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'X-TOKEN': token},
        body: JSON.stringify(body),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        await fetchSocio(id!)
        if (!esSocio) handleCloseDesasociarModal()
        const toast = document.createElement('div')
        toast.className =
          'toast align-items-center text-bg-success border-0 position-fixed top-0 end-0 m-3'
        toast.style.zIndex = '9999'
        toast.innerHTML = `
          <div class="d-flex">
            <div class="toast-body">
              <i class="bi bi-check-circle me-2"></i>
              ${esSocio ? 'Socio asociado' : 'Socio desasociado'} correctamente
            </div>
            <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
          </div>
        `
        document.body.appendChild(toast)
        setTimeout(() => toast.remove(), 3000)
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
        return
      } else {
        throw new Error(data.returned_message || 'Error al cambiar el estado del socio')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setTogglingStatus(false)
      setSubmittingDesasociar(false)
    }
  }

  const handleConfirmDesasociar = async () => {
    const motivoId = parseInt(desasociarForm.motivoId, 10)
    if (!socio || !desasociarForm.motivoId || isNaN(motivoId)) {
      setError('Seleccione un motivo de baja.')
      return
    }
    const motivo = motivosBaja.find((m) => m.id === motivoId)
    const esLicencia = motivo?.nombre?.toLowerCase() === 'licencia' || motivoId === 4
    if (esLicencia && !desasociarForm.fechaReintegro.trim()) {
      setError('Para licencia debe indicar la fecha de reintegro.')
      return
    }
    const now = new Date()
    const fechaBaja =
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
        now.getDate()
      ).padStart(2, '0')} ` +
      `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(
        2,
        '0'
      )}:${String(now.getSeconds()).padStart(2, '0')}`
    const fechaReintegro = desasociarForm.fechaReintegro.trim()
      ? `${desasociarForm.fechaReintegro.trim()} 00:00:00`
      : undefined
    await doAfiliarDesafiliar(false, {
      motivoId,
      fechaBaja,
      observaciones: desasociarForm.observaciones.trim() || '',
      fechaReintegro,
    })
  }

  const handleCancel = () => {
    if (!socio) return

    // Restaurar los valores originales
    const giroIds = socio.giros?.map((g) => g.id) || []
    setSelectedGiroIds(giroIds)

    // Buscar localidad y departamento basándose en la ciudad
    let departamentoId = ''
    let localidadId = ''

    if (socio.ciudad && localidades.length > 0) {
      const localidadEncontrada = localidades.find(
        (loc) => loc.nombre.toLowerCase() === socio.ciudad?.toLowerCase()
      )
      if (localidadEncontrada) {
        localidadId = localidadEncontrada.id.toString()
        departamentoId = localidadEncontrada.departamentoId.toString()
      }
    }

    setFormData({
      nombreFantasia: socio.nombreFantasia || '',
      razonSocial: socio.razonSocial || '',
      rut: socio.rut || '',
      bps: socio.bps || '',
      mtss: socio.mtss || '',
      departamentoId,
      localidadId,
      direccion: socio.direccion || '',
      calle: socio.calle || '',
      numeroPuerta: socio.numeroPuerta || '',
      comentario_direccion: '',
      ciudad: socio.ciudad || '',
      telefono: socio.telefono || '',
      giros: giroIds.join(','),
      contactoNombre: socio.contactoNombre || '',
      contactoTelefono: socio.contactoTelefono || '',
      contactoEmail: socio.contactoEmail || '',
      especialidad: socio.especialidad || '',
      tipoMiembroNombre: 'Potencial',
      clase: 'Empresa',
      claseId: '',
      nroSocio: socio.nroSocio?.toString() || '',
      categoria: socio.categoria?.nombre || '',
      zonaCobro: String(socio.cuenta?.zonaCobro ?? ''),
      activa:
        socio.cuenta?.activa === '1'
          ? 'Activa'
          : socio.cuenta?.activa === '0'
          ? 'Inactiva'
          : 'Activa',
      exentaIva: !!socio.cuenta?.exentoIVA,
      exentaIrae: !!socio.cuenta?.exentoIRAE,
    })
    setIsEditing(false)
    setError(null)
    setBusquedaLocalidad('')
  }

  const handleNewContacto = () => {
    setContactoEditing(null)
    setContactoFormData({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      documentoIdentidad: '',
      idCargo: '',
      activo: true,
      esAdmin: false,
      principal: false,
    })
    setContactoModalOpen(true)
  }

  const handleEditContacto = (contacto: Contacto) => {
    setContactoEditing(contacto)
    setContactoFormData({
      nombre: contacto.nombre || '',
      apellido: contacto.apellido ?? '',
      email: contacto.email || '',
      telefono: contacto.telefono || '',
      documentoIdentidad: contacto.documento_identidad ?? '',
      idCargo: contacto.idCargo != null ? String(contacto.idCargo) : '',
      activo: contacto.activo === undefined || contacto.activo === 1,
      esAdmin: contacto.es_admin === 1,
      principal: contacto.principal === true,
    })
    setContactoModalOpen(true)
  }

  const handleDeleteContacto = async (idContacto: number) => {
    if (!socio || !window.confirm('¿Está seguro de que desea eliminar este contacto?')) return

    try {
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'upsertContacto',
          idContacto: idContacto,
          activo: 0, // Desactivar en lugar de eliminar
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        await fetchSocio(id!)
        const toast = document.createElement('div')
        toast.className =
          'toast align-items-center text-bg-success border-0 position-fixed top-0 end-0 m-3'
        toast.style.zIndex = '9999'
        toast.innerHTML = `
          <div class="d-flex">
            <div class="toast-body">
              <i class="bi bi-check-circle me-2"></i>
              Contacto eliminado correctamente
            </div>
            <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
          </div>
        `
        document.body.appendChild(toast)
        setTimeout(() => toast.remove(), 3000)
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      } else {
        throw new Error(data.returned_message || 'Error al eliminar el contacto')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    }
  }

  const handleValidateRUT = async () => {
    if (!formData.rut.trim()) {
      setError('Debe ingresar un RUT para validar')
      return
    }

    try {
      setValidating(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'existeMiembro',
          nroSocio: '',
          idMiembro: socio ? socio.idMiembro : '',
          rut: formData.rut,
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        const exists = data.data.nombre && data.data.nombre.trim() !== ''
        setValidationModal({
          show: true,
          type: 'rut',
          exists,
          nombre: data.data.nombre || '',
          value: formData.rut,
        })
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      } else {
        throw new Error(data.returned_message || 'Error al validar RUT')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al validar RUT')
    } finally {
      setValidating(false)
    }
  }

  const handleVerifyNroSocio = async () => {
    if (!formData.nroSocio.trim()) {
      setError('Debe ingresar un número de socio para verificar')
      return
    }

    try {
      setVerifyingNroSocio(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'existeMiembro',
          nroSocio: formData.nroSocio,
          idMiembro: '',
          rut: '',
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        const exists = data.data.nombre && data.data.nombre.trim() !== ''
        setValidationModal({
          show: true,
          type: 'nroSocio',
          exists,
          nombre: data.data.nombre || '',
          value: formData.nroSocio,
        })
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      } else {
        throw new Error(data.returned_message || 'Error al verificar número de socio')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al verificar número de socio')
    } finally {
      setVerifyingNroSocio(false)
    }
  }

  const handleSaveContacto = async () => {
    if (!socio) return

    try {
      setSavingContacto(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const idCargoParsed = contactoFormData.idCargo
        ? parseInt(contactoFormData.idCargo, 10)
        : NaN
      const idCargoPayload =
        contactoFormData.idCargo && !Number.isNaN(idCargoParsed) ? {idCargo: idCargoParsed} : {}

      /** Campos comunes al crear y actualizar (mismo contrato que el servicio) */
      const camposContacto = {
        email: contactoFormData.email,
        nombre: contactoFormData.nombre,
        apellido: contactoFormData.apellido,
        telefono: contactoFormData.telefono,
        documentoIdentidad: contactoFormData.documentoIdentidad,
        activo: contactoFormData.activo ? 1 : 0,
        esAdmin: contactoFormData.esAdmin ? 1 : 0,
        principal: contactoFormData.principal,
        ...idCargoPayload,
      }

      const body: Record<string, unknown> = contactoEditing
        ? {
            service: 'upsertContacto',
            idContacto: contactoEditing.id,
            miembroId: parseInt(socio.idMiembro, 10),
            ...camposContacto,
          }
        : {
            service: 'upsertContacto',
            miembroId: parseInt(socio.idMiembro, 10),
            empresaId: parseInt(socio.id, 10),
            ...camposContacto,
          }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        setContactoModalOpen(false)
        await fetchSocio(id!)
        const toast = document.createElement('div')
        toast.className =
          'toast align-items-center text-bg-success border-0 position-fixed top-0 end-0 m-3'
        toast.style.zIndex = '9999'
        toast.innerHTML = `
          <div class="d-flex">
            <div class="toast-body">
              <i class="bi bi-check-circle me-2"></i>
              Contacto ${contactoEditing ? 'actualizado' : 'creado'} correctamente
            </div>
            <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
          </div>
        `
        document.body.appendChild(toast)
        setTimeout(() => toast.remove(), 3000)
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      } else {
        throw new Error(
          data.returned_message ||
            `Error al ${contactoEditing ? 'actualizar' : 'crear'} el contacto`
        )
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setSavingContacto(false)
    }
  }

  useEffect(() => {
    if (!id) return
    fetchSocio(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (!contactoModalOpen) return
    fetchCargos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactoModalOpen])

  useEffect(() => {
    if (!contactoModalOpen || !contactoEditing || loadingCargos || cargosDisponibles.length === 0) return
    if (contactoFormData.idCargo) return
    const c = contactoEditing
    if (c.cargo?.trim()) {
      const match = cargosDisponibles.find(
        (g) => g.nombre.trim().toLowerCase() === c.cargo!.trim().toLowerCase()
      )
      if (match) {
        setContactoFormData((prev) => ({...prev, idCargo: String(match.id)}))
      }
    }
  }, [
    contactoModalOpen,
    contactoEditing,
    loadingCargos,
    cargosDisponibles,
    contactoFormData.idCargo,
  ])

  // Filtrar localidades por departamento y búsqueda
  useEffect(() => {
    if (!formData.departamentoId) {
      setLocalidadesFiltradas([])
      setFormData((prev) => ({...prev, localidadId: ''}))
      return
    }

    const departamentoId = parseInt(formData.departamentoId)
    let filtradas = localidades.filter((loc) => loc.departamentoId === departamentoId)

    // Aplicar filtro de búsqueda si existe
    if (busquedaLocalidad.trim()) {
      const busqueda = busquedaLocalidad.toLowerCase()
      filtradas = filtradas.filter(
        (loc) => loc.nombre.toLowerCase().includes(busqueda) || loc.codigoPostal.includes(busqueda)
      )
    }

    setLocalidadesFiltradas(filtradas)

    // Si la localidad seleccionada no pertenece al departamento actual, limpiarla
    if (formData.localidadId) {
      const localidadSeleccionada = localidades.find(
        (loc) => loc.id.toString() === formData.localidadId
      )
      if (localidadSeleccionada && localidadSeleccionada.departamentoId !== departamentoId) {
        setFormData((prev) => ({...prev, localidadId: ''}))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.departamentoId, busquedaLocalidad, localidades])

  // Manejar cambio de departamento
  const handleDepartamentoChange = (departamentoId: string) => {
    setFormData({
      ...formData,
      departamentoId,
      localidadId: '', // Limpiar localidad al cambiar departamento
    })
    setBusquedaLocalidad('') // Limpiar búsqueda
  }

  // Actualizar departamento y localidad cuando se cargan las localidades y hay un socio
  useEffect(() => {
    if (socio && localidades.length > 0 && socio.ciudad) {
      const localidadEncontrada = localidades.find(
        (loc) => loc.nombre.toLowerCase() === socio.ciudad?.toLowerCase()
      )
      if (localidadEncontrada) {
        setFormData((prev) => ({
          ...prev,
          localidadId: localidadEncontrada.id.toString(),
          departamentoId: localidadEncontrada.departamentoId.toString(),
        }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localidades, socio?.ciudad])

  // Cargar giros, categorías, departamentos y localidades cuando entra en modo edición
  useEffect(() => {
    if (isEditing) {
      fetchGiros()
      fetchCategorias()
      fetchDepartamentos()
      fetchLocalidades()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  const cuenta = socio?.cuenta
  const movimientos = useMemo(() => cuenta?.movimientos || [], [cuenta?.movimientos])
  const totalDebe = useMemo(() => {
    return movimientos
      .filter((mov) => mov.tipo === 'DEBE')
      .reduce((sum, mov) => sum + (parseFloat(mov.monto || '0') || 0), 0)
  }, [movimientos])

  const totalHaber = useMemo(() => {
    return movimientos
      .filter((mov) => mov.tipo === 'HABER')
      .reduce((sum, mov) => sum + (parseFloat(mov.monto || '0') || 0), 0)
  }, [movimientos])

  const saldoFinal = useMemo(
    () => (cuenta?.saldoInicial ?? 0) - totalDebe + totalHaber,
    [cuenta?.saldoInicial, totalDebe, totalHaber]
  )

  const contactosSecundarios = useMemo(() => {
    if (!socio) return []
    return getContactosSecundarios(socio.contactos, {
      contactoNombre: socio.contactoNombre,
      contactoEmail: socio.contactoEmail,
      contactoTelefono: socio.contactoTelefono,
    })
  }, [socio])

  // ============================================================================
  // ESTADOS DE CARGA Y ERROR
  // ============================================================================

  if (loading) {
    return (
      <div className='card'>
        <div className='card-body text-center py-20'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Cargando...</span>
          </div>
          <div className='text-gray-700 fw-semibold fs-6 mt-5'>
            Cargando información del socio...
          </div>
        </div>
      </div>
    )
  }

  if (error && !socio) {
    return (
      <div className='alert alert-danger d-flex align-items-center p-5'>
        <KTIcon iconName='information-5' className='fs-2hx text-danger me-4' />
        <div className='d-flex flex-column'>
          <h4 className='mb-1 text-danger'>Error al cargar los datos</h4>
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (!socio) {
    return (
      <div className='card'>
        <div className='card-body'>
          <div className='alert alert-warning d-flex align-items-center p-5'>
            <KTIcon iconName='information-5' className='fs-2hx text-warning me-4' />
            <div className='d-flex flex-column'>
              <h4 className='mb-1 text-warning'>Socio no encontrado</h4>
              <span>No se encontró el socio solicitado.</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ============================================================================
  // RENDERIZADO PRINCIPAL
  // ============================================================================

  return (
    <>
      <PageTitle>Detalle del Socio</PageTitle>

      {/* Mensaje de error durante la edición */}
      {error && socio && (
        <div className='alert alert-danger d-flex align-items-center p-5 mb-5'>
          <KTIcon iconName='information-5' className='fs-2hx text-danger me-4' />
          <div className='d-flex flex-column'>
            <h4 className='mb-1 text-danger'>Error</h4>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Header del Socio */}
      <div className='card mb-5 mb-xl-10'>
        <div className='card-body pt-9 pb-0'>
          <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
            {/* Avatar/Foto del Socio */}
            {socio.foto && socio.foto.trim() !== '' && (
              <div className='symbol symbol-100px symbol-lg-160px symbol-fixed position-relative me-5 mb-3'>
                <img
                  src={`${process.env.REACT_APP_API_URL?.replace('/services', '')}${socio.foto}`}
                  alt={socio.nombreFantasia}
                  className='symbol-label'
                  style={{
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                  onError={(e) => {
                    // Si falla la carga, ocultar la imagen
                    e.currentTarget.style.display = 'none'
                  }}
                />
              </div>
            )}
            <div className='flex-grow-1'>
              <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                <div className='d-flex flex-column'>
                  <div className='d-flex align-items-center mb-2'>
                    <span className='text-gray-900 text-hover-primary fs-2 fw-bold me-3'>
                      {socio.nombreFantasia}
                    </span>
                    <span
                      className={`badge badge-lg badge-light-${
                        socio.esSocio === 1 ? 'success' : 'danger'
                      }`}
                    >
                      {socio.esSocio === 1 ? 'Asociado' : 'Desasociado'}
                    </span>
                    {isEditing && (
                      <button
                        type='button'
                        className={`btn btn-sm ms-3 ${
                          socio.esSocio === 1 ? 'btn-danger' : 'btn-success'
                        }`}
                        onClick={handleToggleSocioStatus}
                        disabled={togglingStatus}
                        title={socio.esSocio === 1 ? 'Desasociar socio' : 'Asociar socio'}
                      >
                        {togglingStatus ? (
                          <>
                            <span className='spinner-border spinner-border-sm me-2' />
                            Procesando...
                          </>
                        ) : (
                          <>
                            <KTIcon
                              iconName={socio.esSocio === 1 ? 'user-cross' : 'user-tick'}
                              className='fs-3'
                            />
                            {socio.esSocio === 1 ? 'Desasociar' : 'Asociar'}
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'>
                    <span className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                      <KTIcon iconName='profile-circle' className='fs-4 me-1' />
                      Socio N° {socio.nroSocio ?? 'N/A'}
                    </span>
                    <span className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                      <KTIcon iconName='sms' className='fs-4 me-1' />
                      {socio.contactoEmail || 'Sin email'}
                    </span>
                    <span className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                      <KTIcon iconName='phone' className='fs-4 me-1' />
                      {socio.telefono || 'Sin teléfono'}
                    </span>
                  </div>
                </div>

                {/* Botones de Acción */}
                <div className='d-flex gap-2'>
                  {!isEditing ? (
                    <button className='btn btn-sm btn-primary' onClick={() => setIsEditing(true)}>
                      <KTIcon iconName='pencil' className='fs-3' />
                      Editar
                    </button>
                  ) : (
                    <>
                      <button
                        className='btn btn-sm btn-success'
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <span className='spinner-border spinner-border-sm me-2' />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <KTIcon iconName='check' className='fs-3' />
                            Guardar
                          </>
                        )}
                      </button>
                      <button
                        className='btn btn-sm btn-light-danger'
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        <KTIcon iconName='cross' className='fs-3' />
                        Cancelar
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className='d-flex flex-wrap flex-stack'>
                <div className='d-flex flex-column flex-grow-1 pe-8'>
                  <div className='d-flex flex-wrap'>
                    <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                      <div className='d-flex align-items-center'>
                        <KTIcon iconName='category' className='fs-3 text-primary me-2' />
                        <div className='fs-2 fw-bold'>{socio.categoria?.nombre || 'N/A'}</div>
                      </div>
                      <div className='fw-semibold fs-6 text-gray-400'>
                        {socio.categoria?.descripcion || 'Sin categoría'}
                      </div>
                    </div>

                    <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                      <div className='d-flex align-items-center'>
                        <KTIcon iconName='briefcase' className='fs-3 text-success me-2' />
                        <div className='fs-2 fw-bold'>{socio.giros?.length || 0}</div>
                      </div>
                      <div className='fw-semibold fs-6 text-gray-400'>
                        {socio.giros?.length === 1 ? 'Giro' : 'Giros'}
                      </div>
                    </div>

                    {cuenta && (
                      <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                        <div className='d-flex align-items-center'>
                          <KTIcon iconName='wallet' className='fs-3 text-warning me-2' />
                          <div
                            className={`fs-2 fw-bold text-${
                              saldoFinal >= 0 ? 'success' : 'danger'
                            }`}
                          >
                            $ {saldoFinal.toLocaleString()}
                          </div>
                        </div>
                        <div className='fw-semibold fs-6 text-gray-400'>Saldo final</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold'>
            <li className='nav-item mt-2'>
              <span className='nav-link text-active-primary ms-0 me-10 py-5 active'>
                {isEditing ? 'Editando Información' : 'Información General'}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className='row g-5 g-xl-8'>
        {/* Columna Izquierda */}
        <div className='col-xl-6'>
          {/* Información del Socio */}
          <SectionCard title='Información del Socio' icon='profile-circle' className='mb-5 mb-xl-8'>
            <InfoRow label='ID de Miembro' value={socio.idMiembro} icon='barcode' />
            {isEditing ? (
              <div className='d-flex align-items-center py-3 border-bottom border-gray-300'>
                <div className='me-4'>
                  <KTIcon iconName='profile-circle' className='fs-1 text-primary' />
                </div>
                <div className='flex-grow-1'>
                  <label className='text-gray-600 fw-semibold fs-7'>Número de Socio</label>
                  <div className='d-flex gap-2 mt-1'>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      value={formData.nroSocio}
                      onChange={(e) => setFormData({...formData, nroSocio: e.target.value})}
                      placeholder='Ingresa el número de socio'
                    />
                    <button
                      type='button'
                      className='btn btn-sm btn-secondary text-nowrap'
                      onClick={handleVerifyNroSocio}
                      disabled={!formData.nroSocio.trim() || verifyingNroSocio}
                    >
                      {verifyingNroSocio ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-1' />
                          Verificando...
                        </>
                      ) : (
                        <>
                          <KTIcon iconName='verify' className='fs-5 me-1' />
                          Verificar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <InfoRow
                label='Número de Socio'
                value={socio.nroSocio?.toString() || ''}
                icon='profile-circle'
              />
            )}

            {isEditing ? (
              <>
                <EditableInfoRow
                  label='Nombre Fantasía'
                  value={formData.nombreFantasia}
                  onChange={(v) => setFormData({...formData, nombreFantasia: v})}
                  icon='abstract-26'
                  required
                />
                <EditableInfoRow
                  label='Razón Social'
                  value={formData.razonSocial}
                  onChange={(v) => setFormData({...formData, razonSocial: v})}
                  icon='office-bag'
                />
                <div className='d-flex align-items-center py-3 border-bottom border-gray-300'>
                  <div className='me-4'>
                    <KTIcon iconName='document' className='fs-1 text-primary' />
                  </div>
                  <div className='flex-grow-1'>
                    <label className='text-gray-600 fw-semibold fs-7'>
                      RUT <span className='text-danger'>*</span>
                    </label>
                    <div className='d-flex gap-2 mt-1'>
                      <input
                        type='text'
                        className='form-control form-control-sm'
                        value={formData.rut}
                        onChange={(e) => setFormData({...formData, rut: e.target.value})}
                        required
                      />
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={handleValidateRUT}
                        disabled={validating || !formData.rut.trim()}
                        title='Validar RUT'
                      >
                        {validating ? (
                          <span className='spinner-border spinner-border-sm' />
                        ) : (
                          <KTIcon iconName='verify' className='fs-4' />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <EditableInfoRow
                  label='BPS'
                  value={formData.bps}
                  onChange={(v) => setFormData({...formData, bps: v})}
                  icon='shield-tick'
                />
                <EditableInfoRow
                  label='MTSS'
                  value={formData.mtss}
                  onChange={(v) => setFormData({...formData, mtss: v})}
                  icon='shield'
                />
                <EditableInfoRow
                  label='Teléfono (empresa)'
                  value={formData.telefono}
                  onChange={(v) => setFormData({...formData, telefono: v})}
                  icon='phone'
                  type='tel'
                />
                <EditableInfoRow
                  label='Dirección'
                  value={formData.direccion}
                  onChange={(v) => setFormData({...formData, direccion: v})}
                  icon='map'
                />
                <EditableInfoRow
                  label='Ciudad'
                  value={formData.ciudad}
                  onChange={(v) => setFormData({...formData, ciudad: v})}
                  icon='geolocation'
                />
                <EditableInfoRow
                  label='Especialidad'
                  value={formData.especialidad}
                  onChange={(v) => setFormData({...formData, especialidad: v})}
                  icon='cube-3'
                />
                <EditableInfoRow
                  label='Tipo miembro'
                  value={formData.tipoMiembroNombre}
                  onChange={(v) => setFormData({...formData, tipoMiembroNombre: v})}
                  icon='profile-circle'
                />
                <EditableInfoRow
                  label='Clase'
                  value={formData.clase}
                  onChange={(v) => setFormData({...formData, clase: v})}
                  icon='category'
                />
                <EditableInfoRow
                  label='Clase ID'
                  value={formData.claseId}
                  onChange={(v) => setFormData({...formData, claseId: v})}
                  icon='hashtag'
                />
              </>
            ) : (
              <>
                <InfoRow label='Nombre Fantasía' value={socio.nombreFantasia} icon='abstract-26' />
                <InfoRow label='Razón Social' value={socio.razonSocial} icon='office-bag' />
                <InfoRow label='RUT' value={socio.rut} icon='document' />
                <InfoRow label='BPS' value={socio.bps} icon='shield-tick' />
                <InfoRow label='MTSS' value={socio.mtss || 'No especificado'} icon='shield' />
                <InfoRow
                  label='Teléfono'
                  value={socio.telefono || 'No especificado'}
                  icon='phone'
                />
                <InfoRow label='Especialidad' value={socio.especialidad} icon='cube-3' />
              </>
            )}
          </SectionCard>

          {/* Información de Contacto Principal */}
          <SectionCard
            title='Información de Contacto Principal'
            icon='sms'
            className='mb-5 mb-xl-8'
            headerActions={
              contactosSecundarios.length > 0 || isEditing ? (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  onClick={() => setContactosListaModalOpen(true)}
                >
                  <KTIcon iconName='eye' className='fs-4 me-1' />
                  Ver más contactos
                </button>
              ) : undefined
            }
          >
            {isEditing ? (
              <>
                <EditableInfoRow
                  label='Nombre de Contacto'
                  value={formData.contactoNombre}
                  onChange={(v) => setFormData({...formData, contactoNombre: v})}
                  icon='user'
                  required
                />
                <EditableInfoRow
                  label='Teléfono de Contacto'
                  value={formData.contactoTelefono}
                  onChange={(v) => setFormData({...formData, contactoTelefono: v})}
                  icon='phone'
                  type='tel'
                  required
                />
                <EditableInfoRow
                  label='Email de Contacto'
                  value={formData.contactoEmail}
                  onChange={(v) => setFormData({...formData, contactoEmail: v})}
                  icon='sms'
                  type='email'
                  required
                />
              </>
            ) : (
              <>
                <InfoRow label='Nombre de Contacto' value={socio.contactoNombre} icon='user' />
                <InfoRow label='Teléfono de Contacto' value={socio.contactoTelefono} icon='phone' />
                <InfoRow label='Email de Contacto' value={socio.contactoEmail} icon='sms' />
              </>
            )}
          </SectionCard>

          {/* Ubicación */}
          <SectionCard title='Ubicación' icon='geolocation' className='mb-5 mb-xl-8'>
            {isEditing ? (
              <>
                {/* Departamento */}
                <div className='d-flex align-items-center py-3 border-bottom border-gray-300'>
                  <div className='me-4'>
                    <KTIcon iconName='geolocation' className='fs-1 text-primary' />
                  </div>
                  <div className='flex-grow-1'>
                    <label className='text-gray-600 fw-semibold fs-7'>
                      Departamento <span className='text-danger'>*</span>
                    </label>
                    {loadingDepartamentos ? (
                      <div className='text-center py-3'>
                        <div className='spinner-border spinner-border-sm text-primary' />
                      </div>
                    ) : (
                      <select
                        className='form-select form-select-sm mt-1'
                        value={formData.departamentoId}
                        onChange={(e) => handleDepartamentoChange(e.target.value)}
                        required
                      >
                        <option value=''>Seleccionar departamento...</option>
                        {departamentos.map((dept) => (
                          <option key={dept.id} value={dept.id.toString()}>
                            {dept.nombre}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Localidad */}
                <div className='d-flex align-items-center py-3 border-bottom border-gray-300'>
                  <div className='me-4'>
                    <KTIcon iconName='geolocation' className='fs-1 text-primary' />
                  </div>
                  <div className='flex-grow-1'>
                    <label className='text-gray-600 fw-semibold fs-7'>
                      Localidad <span className='text-danger'>*</span>
                    </label>
                    {!formData.departamentoId ? (
                      <div className='alert alert-info mt-1 mb-0 py-2'>
                        <KTIcon iconName='information-5' className='fs-5 me-2' />
                        Debe seleccionar un departamento primero
                      </div>
                    ) : loadingLocalidades ? (
                      <div className='text-center py-3'>
                        <div className='spinner-border spinner-border-sm text-primary' />
                      </div>
                    ) : (
                      <>
                        {/* Campo de búsqueda */}
                        <div className='mb-2 mt-1'>
                          <div className='position-relative'>
                            <KTIcon
                              iconName='magnifier'
                              className='fs-2 position-absolute top-50 start-0 translate-middle-y ms-3'
                            />
                            <input
                              type='text'
                              className='form-control form-control-sm ps-10'
                              placeholder='Buscar localidad por nombre o código postal...'
                              value={busquedaLocalidad}
                              onChange={(e) => setBusquedaLocalidad(e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Selector de localidad */}
                        <select
                          className='form-select form-select-sm'
                          value={formData.localidadId}
                          onChange={(e) => {
                            const locId = e.target.value
                            const loc = localidadesFiltradas.find((l) => l.id.toString() === locId)
                            setFormData({
                              ...formData,
                              localidadId: locId,
                              ciudad: loc?.nombre ?? formData.ciudad,
                            })
                          }}
                          required
                          disabled={localidadesFiltradas.length === 0}
                        >
                          <option value=''>
                            {localidadesFiltradas.length === 0
                              ? 'No hay localidades disponibles'
                              : 'Seleccionar localidad...'}
                          </option>
                          {localidadesFiltradas.map((loc) => (
                            <option key={loc.id} value={loc.id.toString()}>
                              {loc.nombre}
                              {loc.codigoPostal && loc.codigoPostal !== 'N/A'
                                ? ` (${loc.codigoPostal})`
                                : ''}
                            </option>
                          ))}
                        </select>
                        {localidadesFiltradas.length > 0 && (
                          <div className='form-text'>
                            {localidadesFiltradas.length} localidad
                            {localidadesFiltradas.length !== 1 ? 'es' : ''} encontrada
                            {localidadesFiltradas.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <EditableInfoRow
                  label='Calle'
                  value={formData.calle}
                  onChange={(v) => setFormData({...formData, calle: v})}
                  icon='route'
                />
                <EditableInfoRow
                  label='Número de Puerta'
                  value={formData.numeroPuerta}
                  onChange={(v) => setFormData({...formData, numeroPuerta: v})}
                  icon='home'
                />
                <EditableInfoRow
                  label='Comentario de Dirección'
                  value={formData.comentario_direccion}
                  onChange={(v) => setFormData({...formData, comentario_direccion: v})}
                  icon='information-5'
                />
              </>
            ) : (
              <>
                <InfoRow label='Dirección' value={socio.direccion} icon='map' />
                <InfoRow label='Calle' value={socio.calle || 'No especificado'} icon='route' />
                <InfoRow
                  label='Número de Puerta'
                  value={socio.numeroPuerta || 'No especificado'}
                  icon='home'
                />
                <InfoRow
                  label='Ciudad'
                  value={socio.ciudad || 'No especificado'}
                  icon='geolocation'
                />
              </>
            )}
          </SectionCard>
        </div>

        {/* Columna Derecha */}
        <div className='col-xl-6'>
          {/* Categoría */}
          <SectionCard title='Categoría' icon='category' className='mb-5 mb-xl-8'>
            {isEditing ? (
              <div className='py-3'>
                <label className='text-gray-600 fw-semibold fs-7 mb-2'>
                  Categoría <span className='text-danger'>*</span>
                </label>
                {loadingCategorias ? (
                  <div className='text-center py-3'>
                    <div className='spinner-border spinner-border-sm text-primary' />
                    <div className='text-muted fs-7 mt-2'>Cargando categorías...</div>
                  </div>
                ) : (
                  <select
                    className='form-select form-select-lg'
                    value={formData.categoria}
                    onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                    required
                  >
                    <option value=''>Seleccionar categoría...</option>
                    {categorias.map((cat) => (
                      <option key={cat.id} value={cat.nombre}>
                        {cat.nombre} - {cat.descripcion}
                      </option>
                    ))}
                  </select>
                )}
                <div className='form-text'>Selecciona la categoría del socio</div>
              </div>
            ) : (
              <div className='d-flex align-items-center p-5 bg-light-primary rounded'>
                <KTIcon iconName='category' className='fs-2x text-primary me-5' />
                <div className='flex-grow-1'>
                  <div className='text-gray-800 fw-bold fs-3'>
                    {socio.categoria?.nombre || 'N/A'}
                  </div>
                  <div className='text-gray-600 fw-semibold fs-6'>
                    {socio.categoria?.descripcion || 'Sin descripción'}
                  </div>
                  <div className='text-gray-500 fw-semibold fs-7'>
                    ID: {socio.categoria?.id || 'N/A'}
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          {/* Información de Cuenta */}
          {cuenta && (
            <SectionCard
              title='Información de Cuenta'
              icon='wallet'
              className='mb-5 mb-xl-8'
              headerActions={
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  onClick={() => navigate(`/cuentas/${cuenta.numero}`)}
                >
                  <KTIcon iconName='arrow-right' className='fs-3' />
                  Ver Cuenta
                </button>
              }
            >
              <InfoRow label='Número de Cuenta' value={cuenta.numero} icon='financial-schedule' />
              <div className='d-flex align-items-center py-3 border-bottom border-gray-300'>
                <div className='me-4'>
                  <KTIcon iconName='verify' className='fs-1 text-primary' />
                </div>
                <div className='flex-grow-1'>
                  <div className='text-gray-600 fw-semibold fs-7'>Estado de la Cuenta</div>
                  {isEditing ? (
                    <select
                      className='form-select form-select-sm mt-1'
                      value={formData.activa}
                      onChange={(e) => setFormData({...formData, activa: e.target.value})}
                    >
                      <option value='Activa'>Activa</option>
                      <option value='Inactiva'>Inactiva</option>
                    </select>
                  ) : (
                    <div className='fw-bold fs-6 mt-1'>
                      <span
                        className={`badge badge-light-${
                          cuenta.activa === '1' ? 'success' : 'danger'
                        }`}
                      >
                        {cuenta.activa === '1' ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <InfoRow label='Categoría de Cuenta' value={cuenta.categoria} icon='category' />

              {isEditing ? (
                <EditableInfoRow
                  label='Zona de Cobro'
                  value={formData.zonaCobro}
                  onChange={(v) => setFormData({...formData, zonaCobro: v})}
                  icon='geolocation-home'
                />
              ) : (
                <InfoRow label='Zona de Cobro' value={cuenta.zonaCobro} icon='geolocation-home' />
              )}

              <div className='d-flex align-items-center py-3 border-bottom border-gray-300'>
                <div className='me-4'>
                  <KTIcon iconName='security-user' className='fs-1 text-primary' />
                </div>
                <div className='flex-grow-1'>
                  <div className='text-gray-600 fw-semibold fs-7'>Exenciones Tributarias</div>
                  {isEditing ? (
                    <div className='d-flex flex-wrap gap-4 mt-2'>
                      <label className='form-check form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={formData.exentaIva}
                          onChange={(e) => setFormData({...formData, exentaIva: e.target.checked})}
                        />
                        <span className='form-check-label fw-semibold'>Exento IVA</span>
                      </label>
                      <label className='form-check form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={formData.exentaIrae}
                          onChange={(e) => setFormData({...formData, exentaIrae: e.target.checked})}
                        />
                        <span className='form-check-label fw-semibold'>Exento IRAE</span>
                      </label>
                    </div>
                  ) : (
                    <div className='fw-bold fs-6 mt-1'>
                      <span
                        className={`badge badge-light-${
                          cuenta.exentoIVA ? 'success' : 'secondary'
                        } me-2`}
                      >
                        IVA: {cuenta.exentoIVA ? 'Exento' : 'No Exento'}
                      </span>
                      <span
                        className={`badge badge-light-${
                          cuenta.exentoIRAE ? 'success' : 'secondary'
                        }`}
                      >
                        IRAE: {cuenta.exentoIRAE ? 'Exento' : 'No Exento'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>
          )}

          {/* Giros */}
          {((socio.giros && socio.giros.length > 0) || isEditing) && (
            <SectionCard title='Giros Comerciales' icon='briefcase' className='mb-5 mb-xl-8'>
              {isEditing ? (
                <GirosSelector
                  selectedGiros={selectedGiroIds}
                  availableGiros={girosDisponibles}
                  loading={loadingGiros}
                  onChange={(giroIds) => {
                    setSelectedGiroIds(giroIds)
                    setFormData({...formData, giros: giroIds.join(',')})
                  }}
                  onSearch={(query) => fetchGiros(query)}
                />
              ) : (
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted bg-light'>
                        <th className='ps-4 rounded-start'>Giro</th>
                        <th>Estado</th>
                        <th className='text-center rounded-end'>Principal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {socio.giros.map((giro) => (
                        <tr key={giro.id}>
                          <td className='ps-4'>
                            <div className='d-flex align-items-center'>
                              <div className='d-flex justify-content-start flex-column'>
                                <span className='text-dark fw-bold text-hover-primary fs-6'>
                                  {giro.nombre}
                                </span>
                                {giro.descripcion && (
                                  <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                    {giro.descripcion}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`badge badge-light-${giro.activo ? 'success' : 'danger'}`}
                            >
                              {giro.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className='text-center'>
                            {giro.principal ? (
                              <KTIcon iconName='verify' className='fs-2 text-success' />
                            ) : (
                              <span className='text-muted'>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          )}
        </div>
      </div>

      {/* Movimientos de Cuenta */}
      {cuenta && movimientos.length > 0 && !isEditing && (
        <div className='row g-5 g-xl-8'>
          <div className='col-xl-12'>
            <SectionCard title='Movimientos de Cuenta' icon='financial-schedule'>
              {/* Resumen */}
              <div className='row g-5 g-xl-8 mb-8'>
                <div className='col-xl-3'>
                  <div className='card card-flush border border-info h-100'>
                    <div className='card-body d-flex flex-column justify-content-between'>
                      <KTIcon iconName='wallet' className='fs-2hx text-info mb-3' />
                      <div className='fw-bold fs-6 text-gray-400 mb-2'>Saldo inicial</div>
                      <div
                        className={`fs-2x fw-bold text-${
                          (cuenta.saldoInicial ?? 0) >= 0 ? 'success' : 'danger'
                        }`}
                      >
                        $ {(cuenta.saldoInicial ?? 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-xl-3'>
                  <div className='card card-flush border border-primary h-100'>
                    <div className='card-body d-flex flex-column justify-content-between'>
                      <KTIcon iconName='arrow-down' className='fs-2hx text-danger mb-3' />
                      <div className='fw-bold fs-6 text-gray-400 mb-2'>Total Debe</div>
                      <div className='fs-2x fw-bold text-danger'>
                        $ {totalDebe.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-xl-3'>
                  <div className='card card-flush border border-success h-100'>
                    <div className='card-body d-flex flex-column justify-content-between'>
                      <KTIcon iconName='arrow-up' className='fs-2hx text-success mb-3' />
                      <div className='fw-bold fs-6 text-gray-400 mb-2'>Total Haber</div>
                      <div className='fs-2x fw-bold text-success'>
                        $ {totalHaber.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-xl-3'>
                  <div className='card card-flush border border-warning h-100'>
                    <div className='card-body d-flex flex-column justify-content-between'>
                      <KTIcon iconName='wallet' className='fs-2hx text-warning mb-3' />
                      <div className='fw-bold fs-6 text-gray-400 mb-2'>Saldo final</div>
                      <div
                        className={`fs-2x fw-bold text-${saldoFinal >= 0 ? 'success' : 'danger'}`}
                      >
                        $ {saldoFinal.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabla de Movimientos */}
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bold text-muted bg-light'>
                      <th className='ps-4 min-w-100px rounded-start'>Nº Factura</th>
                      <th className='min-w-100px'>ID Pago</th>
                      <th className='min-w-125px'>Fecha</th>
                      <th className='min-w-100px'>Tipo</th>
                      <th className='min-w-125px text-end'>Monto</th>
                      <th className='min-w-100px'>Medio</th>
                      <th className='min-w-100px'>Nro de Recibo</th>
                      <th className='min-w-100px text-center'>Comprobante</th>
                      <th className='min-w-100px text-center rounded-end'>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimientos.map((mov) => (
                      <tr key={mov.id}>
                        <td className='ps-4'>
                          <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                            #{mov.nroFactura}
                          </span>
                        </td>
                        <td>
                          <span className='text-muted fw-semibold text-muted d-block fs-7'>
                            {mov.idPago}
                          </span>
                        </td>
                        <td>
                          <span className='text-dark fw-semibold d-block fs-6'>
                            {new Date(mov.fecha).toLocaleDateString('es-UY', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge badge-light-${
                              mov.tipo === 'DEBE' ? 'danger' : 'success'
                            }`}
                          >
                            {mov.tipo}
                          </span>
                        </td>
                        <td className='text-end'>
                          <span
                            className={`fw-bold fs-6 text-${
                              mov.tipo === 'DEBE' ? 'danger' : 'success'
                            }`}
                          >
                            {mov.tipo === 'DEBE' ? '+' : '-'} ${' '}
                            {(parseFloat(mov.monto || '0') || 0).toLocaleString()}
                          </span>
                        </td>
                        <td>
                          <span className='text-dark fw-semibold d-block fs-7'>
                            {mov.medioPago || '-'}
                          </span>
                        </td>
                        <td>
                          <span className='text-dark fw-semibold d-block fs-7'>
                            {mov.numeroRecibo ?? '-'}
                          </span>
                        </td>
                        <td className='text-center'>
                          {mov.comprobante ? (
                            <a
                              href={`${process.env.REACT_APP_API_URL?.replace('/services', '')}${
                                mov.comprobante
                              }`}
                              onClick={(e) => {
                                e.preventDefault()
                                if (!mov.comprobante) return

                                try {
                                  const baseUrl =
                                    process.env.REACT_APP_API_URL?.replace('/services', '') || ''
                                  const fullUrl = `${baseUrl}${mov.comprobante}`

                                  if (mov.comprobante.includes('sig=')) {
                                    window.open(fullUrl, '_blank')
                                  } else {
                                    const token = getToken()
                                    if (token) {
                                      fetchWithAuth(fullUrl, {
                                        method: 'GET',
                                        headers: {
                                          'X-TOKEN': token,
                                        },
                                      })
                                        .then(async (response) => {
                                          if (response.ok) {
                                            const blob = await response.blob()
                                            const url = window.URL.createObjectURL(blob)
                                            const link = document.createElement('a')
                                            link.href = url
                                            link.download = `comprobante_${Date.now()}.pdf`
                                            document.body.appendChild(link)
                                            link.click()
                                            document.body.removeChild(link)
                                            window.URL.revokeObjectURL(url)
                                          } else {
                                            window.open(fullUrl, '_blank')
                                          }
                                        })
                                        .catch(() => {
                                          window.open(fullUrl, '_blank')
                                        })
                                    } else {
                                      window.open(fullUrl, '_blank')
                                    }
                                  }
                                } catch (err) {
                                  console.error('Error al descargar comprobante:', err)
                                  const baseUrl =
                                    process.env.REACT_APP_API_URL?.replace('/services', '') || ''
                                  window.open(`${baseUrl}${mov.comprobante}`, '_blank')
                                }
                              }}
                              className='btn btn-sm btn-icon btn-light-primary'
                              title='Descargar comprobante'
                            >
                              <KTIcon iconName='file-down' className='fs-3' />
                            </a>
                          ) : (
                            <span className='text-muted fs-7'>-</span>
                          )}
                        </td>
                        <td className='text-center'>
                          {mov.estado ? (
                            <span className='badge badge-light-info'>{mov.estado}</span>
                          ) : (
                            <span className='text-muted'>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        </div>
      )}

      {/* Modal de Contacto */}
      {contactoModalOpen && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
        >
          <div className='modal-dialog modal-dialog-centered modal-lg'>
            <div className='modal-content'>
              <div className='modal-header bg-primary'>
                <h3 className='modal-title text-white'>
                  <KTIcon iconName='user' className='fs-2hx me-3' />
                  {contactoEditing ? 'Editar Contacto' : 'Nuevo Contacto'}
                </h3>
                <button
                  type='button'
                  className='btn btn-icon btn-sm btn-active-light-primary'
                  onClick={() => setContactoModalOpen(false)}
                  disabled={savingContacto}
                >
                  <KTIcon iconName='cross' className='fs-1 text-white' />
                </button>
              </div>
              <div className='modal-body'>
                {error && (
                  <div className='alert alert-danger d-flex align-items-center p-5 mb-5'>
                    <KTIcon iconName='information-5' className='fs-2hx text-danger me-4' />
                    <div className='d-flex flex-column'>
                      <h4 className='mb-1 text-danger'>Error</h4>
                      <span>{error}</span>
                    </div>
                  </div>
                )}
                <div className='row g-3'>
                  <div className='col-md-6'>
                    <label className='form-label fw-bold fs-7'>
                      Nombre <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className='form-control'
                      value={contactoFormData.nombre}
                      onChange={(e) =>
                        setContactoFormData({...contactoFormData, nombre: e.target.value})
                      }
                      required
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label fw-bold fs-7'>
                      Apellido <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className='form-control'
                      value={contactoFormData.apellido}
                      onChange={(e) =>
                        setContactoFormData({...contactoFormData, apellido: e.target.value})
                      }
                      required
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label fw-bold fs-7'>
                      Email <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='email'
                      className='form-control'
                      value={contactoFormData.email}
                      onChange={(e) =>
                        setContactoFormData({...contactoFormData, email: e.target.value})
                      }
                      required
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label fw-bold fs-7'>
                      Teléfono <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='tel'
                      className='form-control'
                      value={contactoFormData.telefono}
                      onChange={(e) =>
                        setContactoFormData({...contactoFormData, telefono: e.target.value})
                      }
                      required
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label fw-bold fs-7'>Cargo</label>
                    {loadingCargos ? (
                      <div className='d-flex align-items-center py-3'>
                        <span className='spinner-border spinner-border-sm text-primary' />
                        <span className='ms-2 text-muted fs-7'>Cargando cargos...</span>
                      </div>
                    ) : (
                      <select
                        className='form-select'
                        value={contactoFormData.idCargo}
                        onChange={(e) =>
                          setContactoFormData({...contactoFormData, idCargo: e.target.value})
                        }
                      >
                        <option value=''>Seleccionar cargo</option>
                        {cargosDisponibles.map((cargo) => (
                          <option key={cargo.id} value={String(cargo.id)}>
                            {cargo.nombre}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label fw-bold fs-7'>Documento de Identidad</label>
                    <input
                      type='text'
                      className='form-control'
                      value={contactoFormData.documentoIdentidad}
                      onChange={(e) =>
                        setContactoFormData({
                          ...contactoFormData,
                          documentoIdentidad: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className='col-md-12'>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='contactoActivo'
                        checked={contactoFormData.activo}
                        onChange={(e) =>
                          setContactoFormData({...contactoFormData, activo: e.target.checked})
                        }
                      />
                      <label className='form-check-label fw-semibold' htmlFor='contactoActivo'>
                        Activo
                      </label>
                    </div>
                  </div>
                  <div className='col-md-12'>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='contactoEsAdmin'
                        checked={contactoFormData.esAdmin}
                        onChange={(e) =>
                          setContactoFormData({...contactoFormData, esAdmin: e.target.checked})
                        }
                      />
                      <label className='form-check-label fw-semibold' htmlFor='contactoEsAdmin'>
                        Es Administrador
                      </label>
                    </div>
                  </div>
                  <div className='col-md-12'>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='contactoPrincipal'
                        checked={contactoFormData.principal}
                        onChange={(e) =>
                          setContactoFormData({...contactoFormData, principal: e.target.checked})
                        }
                      />
                      <label className='form-check-label fw-semibold' htmlFor='contactoPrincipal'>
                        Contacto Principal
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => setContactoModalOpen(false)}
                  disabled={savingContacto}
                >
                  Cancelar
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={handleSaveContacto}
                  disabled={savingContacto}
                >
                  {savingContacto ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <KTIcon iconName='check' className='fs-4 me-2' />
                      Guardar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SocioContactosModal
        show={contactosListaModalOpen}
        onClose={() => setContactosListaModalOpen(false)}
        contactos={contactosSecundarios}
        isEditing={isEditing}
        onEdit={(c) => {
          setContactosListaModalOpen(false)
          handleEditContacto(c)
        }}
        onDelete={handleDeleteContacto}
        onAdd={() => {
          setContactosListaModalOpen(false)
          handleNewContacto()
        }}
      />

      {/* Modal de Validación RUT / Número de Socio */}
      {validationModal.show && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className={`modal-header bg-${validationModal.exists ? 'warning' : 'success'}`}>
                <h3 className='modal-title text-white'>
                  <KTIcon
                    iconName={validationModal.exists ? 'information-5' : 'verify'}
                    className='fs-2hx me-3'
                  />
                  {validationModal.type === 'nroSocio'
                    ? validationModal.exists
                      ? 'Número de Socio ya existe'
                      : 'Número de Socio disponible'
                    : validationModal.exists
                    ? 'RUT ya existe'
                    : 'RUT disponible'}
                </h3>
                <button
                  type='button'
                  className='btn btn-icon btn-sm btn-active-light-primary'
                  onClick={() => setValidationModal({...validationModal, show: false})}
                >
                  <KTIcon iconName='cross' className='fs-1 text-white' />
                </button>
              </div>
              <div className='modal-body'>
                {validationModal.exists ? (
                  <div className='alert alert-warning d-flex align-items-center p-5'>
                    <KTIcon iconName='information-5' className='fs-2hx text-warning me-4' />
                    <div className='d-flex flex-column'>
                      <h4 className='mb-1 text-warning'>
                        {validationModal.type === 'nroSocio'
                          ? 'Atención: Número de Socio duplicado'
                          : 'Atención: RUT duplicado'}
                      </h4>
                      <span>
                        {validationModal.type === 'nroSocio' ? 'El número de socio' : 'El RUT'}{' '}
                        <strong>{validationModal.value}</strong> ya está registrado y pertenece a{' '}
                        <strong>{validationModal.nombre}</strong>.
                      </span>
                      <span className='mt-3'>
                        Si continúas, podrías estar creando un registro duplicado.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className='alert alert-success d-flex align-items-center p-5'>
                    <KTIcon iconName='verify' className='fs-2hx text-success me-4' />
                    <div className='d-flex flex-column'>
                      <h4 className='mb-1 text-success'>
                        {validationModal.type === 'nroSocio'
                          ? 'Número de Socio disponible'
                          : 'RUT disponible'}
                      </h4>
                      <span>
                        {validationModal.type === 'nroSocio' ? 'El número de socio' : 'El RUT'}{' '}
                        <strong>{validationModal.value}</strong> está disponible y puede ser
                        utilizado.
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => setValidationModal({...validationModal, show: false})}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Desasociar socio */}
      {desasociarModalOpen && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          role='dialog'
          aria-modal='true'
          style={{zIndex: 2000, position: 'fixed', inset: 0}}
        >
          <div
            className='modal-dialog modal-dialog-centered'
            role='document'
            style={{position: 'relative', zIndex: 2001}}
          >
            <div className='modal-content'>
              <div className='modal-header bg-danger'>
                <h3 className='modal-title text-white d-flex align-items-center'>
                  <KTIcon iconName='user-cross' className='fs-2 me-2' />
                  Desasociar socio
                </h3>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  aria-label='Cerrar'
                  onClick={handleCloseDesasociarModal}
                >
                  <KTIcon iconName='cross' className='fs-2 text-white' />
                </button>
              </div>
              <div className='modal-body'>
                <div className='mb-5'>
                  <label className='form-label fw-bold required'>Motivo de baja</label>
                  {loadingMotivosBaja ? (
                    <div className='d-flex align-items-center'>
                      <span className='spinner-border spinner-border-sm me-2' />
                      <span className='text-muted fs-7'>Cargando motivos...</span>
                    </div>
                  ) : (
                    <select
                      className='form-select form-select-solid'
                      value={desasociarForm.motivoId}
                      onChange={(e) =>
                        setDesasociarForm({...desasociarForm, motivoId: e.target.value})
                      }
                    >
                      <option value=''>Seleccionar motivo...</option>
                      {motivosBaja.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.nombre}
                          {m.descripcion ? ` - ${m.descripcion}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className='mb-5'>
                  <label className='form-label fw-bold'>Detalles</label>
                  <textarea
                    className='form-control form-control-solid'
                    rows={3}
                    placeholder='Observaciones, motivo del socio, detalles de la aprobación...'
                    value={desasociarForm.observaciones}
                    onChange={(e) =>
                      setDesasociarForm({...desasociarForm, observaciones: e.target.value})
                    }
                  />
                </div>
                {(desasociarForm.motivoId === '4' ||
                  motivosBaja
                    .find((m) => m.id === parseInt(desasociarForm.motivoId, 10))
                    ?.nombre?.toLowerCase() === 'licencia') && (
                  <div className='mb-0'>
                    <label className='form-label fw-bold required'>
                      Fecha de reintegro (hasta cuándo solicita licencia)
                    </label>
                    <input
                      type='date'
                      className='form-control form-control-solid'
                      value={desasociarForm.fechaReintegro}
                      onChange={(e) =>
                        setDesasociarForm({...desasociarForm, fechaReintegro: e.target.value})
                      }
                    />
                  </div>
                )}
              </div>
              <div className='modal-footer border-0'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={handleCloseDesasociarModal}
                  disabled={submittingDesasociar}
                >
                  Cancelar
                </button>
                <button
                  type='button'
                  className='btn btn-danger'
                  onClick={handleConfirmDesasociar}
                  disabled={submittingDesasociar || !desasociarForm.motivoId || loadingMotivosBaja}
                >
                  {submittingDesasociar ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Procesando...
                    </>
                  ) : (
                    'Confirmar desasociar'
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show' style={{zIndex: 1990}} />
        </div>
      )}
    </>
  )
}

export default SocioDetallePage
